import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@etherisc/ui-kit';
import { UserForm } from '../../components/UserForm';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import type { UserFormData } from '../../schemas/userSchema';
import type { User } from '../../types/user';
import { userService } from '../../services/userService';
import { roleService } from '../../services/roleService';
import {
  ChevronLeftIcon,
} from 'lucide-react';

export function EditUserPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  
  // Memoize toast functions to prevent infinite re-renders
  const showSuccess = useCallback((message: string, description?: string) => 
    toast.success(message, { description }), []);
  const showError = useCallback((message: string, description?: string) => 
    toast.error(message, { description }), []);
  
  const [loading, setLoading] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  
  const isAdmin = currentUser?.roles?.includes('admin') ?? false;
  const isProductAdmin = currentUser?.roles?.includes('product_admin') ?? false;
  const { hasPermission } = usePermissions();
  const canAssignRoles = hasPermission('users:assign_roles');

  // Fetch all roles from backend (includes custom roles)
  const [allRoleNames, setAllRoleNames] = useState<string[]>([]);
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const roles = await roleService.getRoles();
        setAllRoleNames(roles.map(r => r.name));
      } catch {
        setAllRoleNames(['admin', 'system_operator', 'product_admin', 'reinsurer', 'user']);
      }
    };
    loadRoles();
  }, []);

  const availableRoles = useMemo(() => {
    const roleNames = allRoleNames.length > 0
      ? allRoleNames
      : ['admin', 'system_operator', 'product_admin', 'reinsurer', 'user'];

    if (isAdmin || canAssignRoles) {
      return roleNames;
    }

    if (isProductAdmin) {
      return ['user'];
    }

    return ['user'];
  }, [isAdmin, isProductAdmin, canAssignRoles, allRoleNames]);

  // Fetch organizations from database (only for admins)
  const { 
    organizations, 
    loading: organizationsLoading, 
    error: organizationsError 
  } = useOrganizations({ limit: 100, autoFetch: isAdmin }); // Only fetch for admins

  // Load user data on component mount
  useEffect(() => {
    const loadUser = async () => {
      if (!id) {
        showError('Invalid User', 'No user ID provided.');
        navigate('/admin/users');
        return;
      }

      try {
        setLoadingUser(true);
        const user = await userService.getUserById(id);
        setUserToEdit(user);
      } catch (error) {
        console.error('Error loading user:', error);
        showError('Loading Failed', 'Failed to load user data. Please try again.');
        navigate('/admin/users');
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, [id, navigate, showError]); // Dependencies: id, navigate, and showError

  // Filter organizations based on current user's role (same logic as CreateUser)
  const availableOrganizations = useMemo(() => {
    // Admin sees all organizations from API
    if (isAdmin) {
      return organizations.map(org => ({
        id: org.id,
        name: org.name
      }));
    }
    
    // Product admin can only see their own organization
    if (isProductAdmin && currentUser?.organization) {
      return [{
        id: currentUser.organization.id,
        name: currentUser.organization.name
      }];
    }
    
    // Default: show all
    return organizations.map(org => ({
      id: org.id,
      name: org.name
    }));
  }, [organizations, isAdmin, isProductAdmin, currentUser?.organization]);

  const handleSubmit = async (data: UserFormData) => {
    if (!id) return;
    
    setLoading(true);
    try {
      await userService.updateUser(id, data);
      showSuccess('User Updated', `User ${data.name} has been successfully updated.`);
      navigate('/admin/users');
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update user. Please try again.';
      showError('Update Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while user data is being fetched
  if (loadingUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  // Show error state if user not found
  if (!userToEdit) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">User Not Found</h2>
        <p className="text-gray-600 mb-4">The requested user could not be found.</p>
        <button
          onClick={() => navigate('/admin/users')}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Back to User Management
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/admin/users')}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-md"
        >
          <ChevronLeftIcon size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit User</h1>
          <p className="text-gray-600 mt-1">
            Update user information, roles, and permissions.
          </p>
        </div>
      </div>

      <UserForm
        mode="edit"
        user={userToEdit}
        onSubmit={handleSubmit}
        loading={loading || organizationsLoading}
        availableRoles={availableRoles}
        availableOrganizations={availableOrganizations}
        organizationsError={organizationsError}
      />
    </div>
  );
} 